import { Badge } from '@/components/ui/atoms/badge'
import { cn, getPrice } from '@/lib/client'
import type { IProduct } from '@/types/product'

import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/atoms/card'
import type { IPaymentStandard } from '@/types/payment'

export default function ProductsListItem({
  product,
  payment,
}: {
  product: IProduct
  payment: IPaymentStandard
}) {
  const from = product.discountedPrice ? product.price : null
  const to =
    (product.discountedPrice || product.price) *
    (1 - (payment?.in_cash?.greater || 0) / 100)

  return (
    <a href={product.permanentLink}>
      <Card
        className={cn(
          'border-b border-zinc-100 border-2 shadow-lg',
          product.percentage_off && 'border-emerald-500',
        )}
      >
        <CardHeader className="p-4">
          <div className="flex justify-between items-center space-x-4">
            {product.percentage_off ? (
              <Badge
                className={'bg-emerald-500'}
                variant="flag"
                bg="bg-emerald-500"
                border_r="border-r-emerald-500"
                border_t="border-t-emerald-500"
              >
                {Math.round(product.percentage_off)}% OFF
              </Badge>
            ) : (
              <div />
            )}
            <img
              loading="lazy"
              src={product.icon || '/images/logo-color-thin.svg'}
              className="object-contain rounded-lg p-2 bg-zinc-100 h-16 w-16"
              width={64}
              height={64}
              alt={`Curso - ${product.name}`}
            />
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {product.situation && (
            <Badge
              variant="default"
              className={cn(
                ['Previsto', 'Anunciado'].includes(product.situation) &&
                  'text-rose-600 bg-rose-100/80 hover:bg-rose-100',
                ['Banca Definida', 'Comissão Formada'].includes(
                  product.situation,
                ) && 'text-sky-600 bg-sky-100/80 hover:bg-sky-100',
                ['Edital a qualquer momento'].includes(product.situation) &&
                  'text-amber-600 bg-amber-100/80 hover:bg-amber-100',
                ['Autorizado', 'Edital Publicado'].includes(
                  product.situation,
                ) && 'text-emerald-600 bg-emerald-100/80 hover:bg-emerald-100',
              )}
            >
              {product.situation}
            </Badge>
          )}
          <h2 className="text-md text-zinc-600 max-w-[250px] min-h-[120px] max-h-[120px] line-clamp-5 font-semibold mt-3">
            {product.name}
          </h2>
        </CardContent>
        <CardFooter className="flex flex-col items-start p-4">
          {from ? (
            <div className="flex flex-col">
              <span className="text-md leading-3 text-zinc-500">
                De <span className="line-through">{getPrice(from)}</span>
              </span>
              <span className="text-md leading-3">
                Por {product.quantityInstallments}x de{' '}
                <strong
                  className={cn(
                    'text-2xl',
                    product.percentage_off && 'text-emerald-500',
                  )}
                >
                  {getPrice(product.installmentValue)}
                </strong>
              </span>
            </div>
          ) : (
            <div className="flex flex-col">
              <span className="text-xs leading-3">
                {product.quantityInstallments}x de
              </span>
              <strong
                className={cn(
                  'text-2xl',
                  product.percentage_off && 'text-emerald-500',
                )}
              >
                {getPrice(product.installmentValue)}
              </strong>
            </div>
          )}

          <strong
            className={cn(
              'text-sm',
              product.percentage_off && 'text-emerald-500',
            )}
          >
            Ou {getPrice(to)} à vista
          </strong>
        </CardFooter>
      </Card>
    </a>
  )
}
